import { ClientActivityLog, Client as ClientModel, ClientProfileOverview } from '@spike/client-model';
import { ClientsStatus, getClientActivityLogThunk, getClientProfileOverviewThunk } from '@spike/clients-action';
import Invoice from '@spike/invoice-model';
import { getInvoiceThunk, InvoicesStatus, reset as resetInvoices, voidInvoicesThunk } from '@spike/invoices-action';
import { showError, showSuccess } from '@spike/notifications-action';
import { fetchPaymentsWithoutPaginationThunk, PaymentsStatus } from '@spike/payments-action';
import useNonInitialEffect from '@versiondos/hooks';
import clsx from 'clsx';
import Checkout from 'components/Checkout2/Checkout';
import InvoiceView from 'components/OrderDetail/InvoiceView';
import VoidInvoiceModal from 'components/Payments/Transactions/Invoices/Void/VoidInvoiceModal';
import { OverDetail, Spinner } from 'components/UI';
import { useApiClientWrapper, useTimeZone } from 'hooks';
import { useDateRangeOptions } from 'hooks/useDateRangeOptions';
import { useDebounce } from 'hooks/useDebounce';
import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { ClientEditHeader } from './ClientEditHeader';
import {
    ActivityLogDateParams,
    buildActivityLogCategoryOptions,
    formatActivityLogCategoryParam,
    getActivityLogDateParams,
    formatOverviewCreatedAt,
    formatOverviewMoney,
    formatOverviewShowRate,
    hasOutstandingBalance,
    mapActivityLogToOverview,
    mapProfileInvoiceToOverview,
    mapPaymentWithRefundsToOverview
} from './clientOverviewUtils';
import ClientOverviewActivityLog from './UI/Overview/ClientOverviewActivityLog';
import ClientOverviewHeader from './UI/Overview/ClientOverviewHeader';
import ClientOverviewInvoicesPanel from './UI/Overview/ClientOverviewInvoicesPanel';
import ClientOverviewInvoicesTable from './UI/Overview/ClientOverviewInvoicesTable';
import ClientOverviewMetricCard from './UI/Overview/ClientOverviewMetricCard';
import ClientOverviewTransactionHistory from './UI/Overview/ClientOverviewTransactionHistory';
import { TransactionsKey } from 'pages/Client';
import styles from './ClientOverview.module.scss';
import { Payment } from '@spike/payments-model';
import { Period } from '@spike/model';

interface ClientOverviewProps {
    client: ClientModel;
    onBook: () => void;
    onNewSale?: () => void;
    onClose: () => void;
    onSelect: (key: string) => void;
    showTransactions?: boolean;
}

const ACTIVITY_LOG_PAGE_SIZE = 10;
const ACTIVITY_LOG_SEARCH_DEBOUNCE_MS = 500;
const ACTIVITY_LOG_SEARCH_MIN_LENGTH = 3;
const OVERVIEW_TRANSACTIONS_PAGE_SIZE = 10;

type PendingInvoiceAction = 'view' | 'checkout' | 'void';

interface CheckoutState {
    orderId?: number;
    bookingId?: number;
}

export const ClientOverview: FunctionComponent<ClientOverviewProps> = props => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const timezone = useTimeZone();
    const { last30DaysOption } = useDateRangeOptions();

    const clientsStatus = useSelector<RootState, ClientsStatus>(state => state.clients.status);
    const profileOverview = useSelector<RootState, ClientProfileOverview | undefined>(
        state => state.clients.profileOverview
    );
    const activityLog = useSelector<RootState, Array<ClientActivityLog> | undefined>(
        state => state.clients.activityLog
    );
    const invoice = useSelector<RootState, Invoice | undefined>(state => state.invoices.invoice);
    const invoicesStatus = useSelector<RootState, InvoicesStatus>(state => state.invoices.status);
    const paymentsWithoutPagination = useSelector<RootState, Payment[]>(
        state => state.payments.paymentsWithoutPagination
    );
    const paymentsStatus = useSelector<RootState, PaymentsStatus>(state => state.payments.status);

    const [showInvoiceView, setShowInvoiceView] = useState<Invoice | undefined>();
    const [pendingInvoiceAction, setPendingInvoiceAction] = useState<{
        invoiceId: number;
        action: PendingInvoiceAction;
    } | null>(null);
    const [showCheckout, setShowCheckout] = useState<CheckoutState | null>(null);
    const [checkoutLoadingInvoiceId, setCheckoutLoadingInvoiceId] = useState<number | null>(null);
    const [invoiceToVoid, setInvoiceToVoid] = useState<Invoice | null>(null);
    const pendingVoidRef = useRef(false);
    const voidingInvoice = invoicesStatus === InvoicesStatus.VoidingInvoice;
    const [activitySearchText, setActivitySearchText] = useState('');
    const [activityLogPage, setActivityLogPage] = useState(1);
    const [activityLogPageSize, setActivityLogPageSize] = useState(ACTIVITY_LOG_PAGE_SIZE);
    const debouncedActivitySearch = useDebounce(activitySearchText, ACTIVITY_LOG_SEARCH_DEBOUNCE_MS);
    const activityLogEventRef = useRef<string | undefined>(undefined);
    const [activityLogPeriod, setActivityLogPeriod] = useState<Period | null>(last30DaysOption.value);
    const [selectedActivityCategories, setSelectedActivityCategories] = useState<string[] | undefined>(undefined);
    const activityLogDateRef = useRef<ActivityLogDateParams>(getActivityLogDateParams(last30DaysOption.value));
    const activityLogCategoryRef = useRef<string | undefined>(undefined);
    const lastActivityLogRequestRef = useRef<{
        clientId?: number;
        event?: string;
        page?: number;
        size?: number;
        dateStart?: string;
        dateEnd?: string;
        category?: string;
    }>({});

    const activityLogTotalPages = useSelector<RootState, number>(state => Math.max(1, state.clients.total_pages ?? 1));
    const isActivityLogLoading = clientsStatus === ClientsStatus.ActivityLogGetting;

    const fetchActivityLog = (
        page: number,
        size: number,
        event?: string,
        dateParams: ActivityLogDateParams = activityLogDateRef.current,
        category: string | undefined = activityLogCategoryRef.current
    ) => {
        if (!props.client.id) return;

        const { dateStart, dateEnd } = dateParams;
        const lastRequest = lastActivityLogRequestRef.current;
        if (
            lastRequest.clientId === props.client.id &&
            lastRequest.event === event &&
            lastRequest.page === page &&
            lastRequest.size === size &&
            lastRequest.dateStart === dateStart &&
            lastRequest.dateEnd === dateEnd &&
            lastRequest.category === category
        ) {
            return;
        }

        lastActivityLogRequestRef.current = {
            clientId: props.client.id,
            event,
            page,
            size,
            dateStart,
            dateEnd,
            category
        };

        dispatch(
            getClientActivityLogThunk(
                apiClientWrapper,
                props.client.id,
                page,
                size,
                category,
                event,
                dateStart,
                dateEnd
            )
        );
    };

    const handleActivityLogPeriodChange = (period: Period | null) => {
        const dateParams = getActivityLogDateParams(period);

        setActivityLogPeriod(period);
        activityLogDateRef.current = dateParams;
        setActivityLogPage(1);
        fetchActivityLog(1, activityLogPageSize, activityLogEventRef.current, dateParams);
    };

    const handleActivityLogCategoriesChange = (categories: string[] | undefined) => {
        const categoryParam = formatActivityLogCategoryParam(categories);

        setSelectedActivityCategories(categories);
        activityLogCategoryRef.current = categoryParam;
        setActivityLogPage(1);
        fetchActivityLog(
            1,
            activityLogPageSize,
            activityLogEventRef.current,
            activityLogDateRef.current,
            categoryParam
        );
    };

    const handleActivityLogPageChange = (page: number) => {
        setActivityLogPage(page);
        fetchActivityLog(page, activityLogPageSize, activityLogEventRef.current);
    };

    const handleActivityLogPageSizeChange = (size: number) => {
        setActivityLogPage(1);
        setActivityLogPageSize(size);
        fetchActivityLog(1, size, activityLogEventRef.current);
    };

    const dispatchClientPaymentsFetch = () => {
        if (!props.client.id) return;

        dispatch(fetchPaymentsWithoutPaginationThunk(apiClientWrapper, props.client.id, null));
    };

    useEffect(() => {
        if (!props.client.id) return;

        setActivitySearchText('');
        setActivityLogPage(1);
        setActivityLogPageSize(ACTIVITY_LOG_PAGE_SIZE);
        setActivityLogPeriod(last30DaysOption.value);
        setSelectedActivityCategories(undefined);
        activityLogEventRef.current = undefined;
        activityLogDateRef.current = getActivityLogDateParams(last30DaysOption.value);
        activityLogCategoryRef.current = undefined;
        lastActivityLogRequestRef.current = {};
        dispatch(getClientProfileOverviewThunk(apiClientWrapper, props.client.id));
    }, [props.client.id]);

    useEffect(() => {
        if (!props.client.id) return;
        if (debouncedActivitySearch !== activitySearchText) return;

        const event =
            debouncedActivitySearch.length >= ACTIVITY_LOG_SEARCH_MIN_LENGTH ? debouncedActivitySearch : undefined;

        if (debouncedActivitySearch.length < ACTIVITY_LOG_SEARCH_MIN_LENGTH && debouncedActivitySearch.length > 0) {
            return;
        }

        activityLogEventRef.current = event;
        setActivityLogPage(1);
        fetchActivityLog(1, activityLogPageSize, event);
    }, [debouncedActivitySearch, activitySearchText, props.client.id, activityLogPageSize]);

    useEffect(() => {
        dispatchClientPaymentsFetch();
    }, [props.client.id]);

    const openInvoiceHandler = (invoiceId: number) => {
        setPendingInvoiceAction({ invoiceId, action: 'view' });
        dispatch(getInvoiceThunk(apiClientWrapper, invoiceId));
    };

    const checkoutInvoiceHandler = (invoiceId: number) => {
        if (checkoutLoadingInvoiceId !== null) return;

        setCheckoutLoadingInvoiceId(invoiceId);
        setPendingInvoiceAction({ invoiceId, action: 'checkout' });
        dispatch(getInvoiceThunk(apiClientWrapper, invoiceId));
    };

    const voidInvoiceRequestHandler = (invoiceId: number) => {
        setPendingInvoiceAction({ invoiceId, action: 'void' });
        dispatch(getInvoiceThunk(apiClientWrapper, invoiceId));
    };

    const closeVoidInvoiceHandler = () => {
        if (voidingInvoice) return;

        setInvoiceToVoid(null);
        dispatch(resetInvoices());
    };

    const confirmVoidInvoiceHandler = () => {
        if (!invoiceToVoid?.id) return;

        pendingVoidRef.current = true;
        dispatch(voidInvoicesThunk(apiClientWrapper, invoiceToVoid.id));
    };

    const closeInvoiceHandler = () => {
        setShowInvoiceView(undefined);
        setPendingInvoiceAction(null);
        dispatch(resetInvoices());
    };

    const closeCheckoutHandler = () => {
        setShowCheckout(null);
        setCheckoutLoadingInvoiceId(null);
        dispatch(resetInvoices());
        if (props.client.id) {
            dispatch(getClientProfileOverviewThunk(apiClientWrapper, props.client.id));
            dispatchClientPaymentsFetch();
        }
    };

    const startCheckoutFromInvoice = (loadedInvoice: Invoice) => {
        dispatch(resetInvoices());
        setCheckoutLoadingInvoiceId(null);

        if (loadedInvoice.orderId || loadedInvoice.bookingId) {
            setShowCheckout({
                orderId: loadedInvoice.orderId ?? undefined,
                bookingId: loadedInvoice.bookingId ?? undefined
            });
            return;
        }

        dispatch(showError('Unable to open checkout for this invoice.'));
    };

    useNonInitialEffect(() => {
        if (!pendingInvoiceAction) return;
        if (invoicesStatus !== InvoicesStatus.GetInvoiceSuccess || !invoice) return;
        if (invoice.id !== pendingInvoiceAction.invoiceId) return;

        const { action } = pendingInvoiceAction;
        setPendingInvoiceAction(null);

        if (action === 'view') {
            setShowInvoiceView(invoice);
            return;
        }

        if (action === 'void') {
            if (invoice.voidable) {
                setInvoiceToVoid(invoice);
            } else {
                dispatch(resetInvoices());
                dispatch(showError('This invoice cannot be voided.'));
            }
            return;
        }

        startCheckoutFromInvoice(invoice);
    }, [invoicesStatus, invoice, pendingInvoiceAction]);

    useNonInitialEffect(() => {
        if (!pendingVoidRef.current) return;

        if (invoicesStatus === InvoicesStatus.VoidInvoiceSuccess) {
            pendingVoidRef.current = false;
            setInvoiceToVoid(null);
            dispatch(resetInvoices());
            dispatch(showSuccess('Invoice voided.'));
            if (props.client.id) {
                dispatch(getClientProfileOverviewThunk(apiClientWrapper, props.client.id));
                dispatchClientPaymentsFetch();
            }
        } else if (invoicesStatus === InvoicesStatus.Error) {
            pendingVoidRef.current = false;
        }
    }, [invoicesStatus]);

    useNonInitialEffect(() => {
        if (checkoutLoadingInvoiceId === null) return;
        if (invoicesStatus !== InvoicesStatus.Error) return;

        setCheckoutLoadingInvoiceId(null);
        setPendingInvoiceAction(null);
    }, [invoicesStatus, checkoutLoadingInvoiceId]);

    const isProfileOverviewLoading =
        !profileOverview &&
        (clientsStatus === ClientsStatus.ProfileOverviewGetting || clientsStatus === ClientsStatus.Getting);

    const headerTags = useMemo(() => {
        if (profileOverview?.customer.tags?.length) {
            return profileOverview.customer.tags;
        }
        return [
            ...props.client.tags.positive.map(tag => tag.name),
            ...props.client.tags.negative.map(tag => tag.name)
        ].filter(Boolean);
    }, [profileOverview, props.client.tags]);

    const createdAtLabel = useMemo(() => {
        if (profileOverview?.customer.createdAt) {
            return formatOverviewCreatedAt(profileOverview.customer.createdAt);
        }
        return '';
    }, [profileOverview]);

    const stats = useMemo(() => {
        if (!profileOverview) return [];
        const { overview } = profileOverview;
        return [
            { label: 'Total Sales', value: formatOverviewMoney(overview.totalSales) },
            { label: 'Total Visits', value: String(overview.totalVisits ?? 0) },
            { label: '% Show Rate', value: formatOverviewShowRate(overview.showRate) },
            { label: 'Canceled by Client', value: String(overview.canceledByCustomer ?? 0) },
            { label: 'No Show', value: String(overview.noShow ?? 0) }
        ];
    }, [profileOverview]);

    const outstanding = useMemo(() => {
        if (!profileOverview) return [];
        const { outstandingInvoices } = profileOverview;
        const invoiceDebt = outstandingInvoices.invoiceDebt;
        const debtAmount = typeof invoiceDebt === 'string' ? parseFloat(invoiceDebt) : invoiceDebt ?? 0;

        return [
            {
                label: 'Unpaid Invoices',
                value: formatOverviewMoney(invoiceDebt),
                danger: debtAmount !== 0,
                pill: debtAmount > 0 ? 'Owes' : undefined
            }
        ];
    }, [profileOverview]);

    const clientPayments = useMemo(() => paymentsWithoutPagination, [paymentsWithoutPagination]);

    const outstandingInvoices = useMemo(
        () =>
            profileOverview?.outstandingInvoices.invoices
                .filter(invoice => hasOutstandingBalance(invoice.remaining))
                .map(invoice => mapProfileInvoiceToOverview(invoice, timezone)) ?? [],
        [profileOverview, timezone]
    );

    const invoices = useMemo(
        () => profileOverview?.invoices.map(invoice => mapProfileInvoiceToOverview(invoice, timezone)) ?? [],
        [profileOverview, timezone]
    );

    const transactions = useMemo(() => {
        if (paymentsStatus !== PaymentsStatus.FetchPaymentsWithoutPaginationSuccess) {
            return [];
        }

        return clientPayments
            .slice(0, OVERVIEW_TRANSACTIONS_PAGE_SIZE)
            .flatMap(payment => mapPaymentWithRefundsToOverview(payment, timezone));
    }, [paymentsStatus, clientPayments, timezone]);

    const isTransactionsLoading =
        Boolean(props.client.id) &&
        paymentsStatus !== PaymentsStatus.FetchPaymentsWithoutPaginationSuccess &&
        paymentsStatus !== PaymentsStatus.Error;

    const activityLogItems = useMemo(
        () => activityLog?.map(entry => mapActivityLogToOverview(entry, timezone)) ?? [],
        [activityLog, timezone]
    );

    const activityLogCategoryOptions = useMemo(
        () => buildActivityLogCategoryOptions(activityLog?.map(entry => entry.category)),
        [activityLog]
    );

    return (
        <div className={styles.root}>
            <div className={styles.content}>
                <ClientEditHeader
                    client={props.client}
                    onBook={props.onBook}
                    onNewSale={props.onNewSale}
                    onClose={props.onClose}
                    onSelect={props.onSelect}
                    showTransactions={props.showTransactions}
                />

                <div className={styles.header}>
                    <ClientOverviewHeader
                        clientName={`${props.client.firstName} ${props.client.lastName}`}
                        createdAt={createdAtLabel}
                        tags={headerTags}
                    />
                </div>

                <div className={styles.overviewContainer}>
                    <div className={styles.box}>
                        <h2 className={styles.sectionTitle}>Overview</h2>
                    </div>
                    {isProfileOverviewLoading ? (
                        <div className={styles.loadingContainer}>
                            <Spinner />
                        </div>
                    ) : (
                        <>
                            <div className={styles.box}>
                                <div className={styles.statsGrid}>
                                    {stats.map(stat => (
                                        <ClientOverviewMetricCard
                                            key={stat.label}
                                            label={stat.label}
                                            value={stat.value}
                                        />
                                    ))}
                                </div>

                                <h2 className={clsx(styles.sectionTitle, styles.plainSectionTitle)}>
                                    Outstanding Invoices
                                </h2>
                                <div className={clsx(styles.miniCardsGrid, styles.miniCardsGridSingle)}>
                                    {outstanding.map(item => (
                                        <ClientOverviewMetricCard
                                            key={item.label}
                                            label={item.label}
                                            value={item.value}
                                            pill={item.pill}
                                            danger={item.danger}
                                        />
                                    ))}
                                </div>
                            </div>
                            <div className={styles.invoicesTableContainer}>
                                {outstandingInvoices.length > 0 ? (
                                    <ClientOverviewInvoicesTable
                                        invoices={outstandingInvoices}
                                        onOpenInvoice={openInvoiceHandler}
                                        onCheckout={checkoutInvoiceHandler}
                                        onVoid={voidInvoiceRequestHandler}
                                        checkoutLoadingInvoiceId={checkoutLoadingInvoiceId}
                                    />
                                ) : (
                                    <p className={styles.noOutstandingInvoices}>No outstanding invoices.</p>
                                )}
                            </div>
                        </>
                    )}

                    <div className={styles.overviewPanels}>
                        <div className={styles.left}>
                            <ClientOverviewTransactionHistory
                                transactions={transactions}
                                isLoading={isTransactionsLoading}
                                onViewAll={props.showTransactions ? () => props.onSelect(TransactionsKey) : undefined}
                            />
                        </div>
                        <div className={styles.right}>
                            <ClientOverviewInvoicesPanel invoices={invoices} />
                        </div>
                        <ClientOverviewActivityLog
                            activityLog={activityLogItems}
                            searchText={activitySearchText}
                            searchInputKey={props.client.id}
                            datePeriod={activityLogPeriod}
                            categories={activityLogCategoryOptions}
                            selectedCategories={selectedActivityCategories}
                            onSearchChange={setActivitySearchText}
                            onDatePeriodChange={handleActivityLogPeriodChange}
                            onCategoriesChange={handleActivityLogCategoriesChange}
                            pagination={{
                                page: activityLogPage,
                                totalPages: activityLogTotalPages,
                                size: activityLogPageSize,
                                isLoading: isActivityLogLoading
                            }}
                            onPageChange={handleActivityLogPageChange}
                            onPageSizeChange={handleActivityLogPageSizeChange}
                        />
                    </div>
                </div>
            </div>

            {showInvoiceView && (
                <OverDetail pageSidebar>
                    <InvoiceView
                        invoice={showInvoiceView}
                        onBack={closeInvoiceHandler}
                    />
                </OverDetail>
            )}
            <VoidInvoiceModal
                invoice={invoiceToVoid}
                voiding={voidingInvoice}
                zIndex={10100}
                onClose={closeVoidInvoiceHandler}
                onConfirm={confirmVoidInvoiceHandler}
            />
            {showCheckout && (
                <Checkout
                    orderId={showCheckout.orderId}
                    bookingId={showCheckout.bookingId}
                    zIndex={10100}
                    onClose={closeCheckoutHandler}
                    onPayByLinkSent={closeCheckoutHandler}
                    onBack={closeCheckoutHandler}
                    onPaid={closeCheckoutHandler}
                />
            )}
        </div>
    );
};

export default ClientOverview;
